import React, { useCallback, useState, useRef } from 'react';
import type { Block, BlockType } from '../types';
import { BlockItem } from './Block';
import { CommandMenu } from './CommandMenu';
interface BlockEditorProps {
  blocks: Block[];
  onAddBlock: (afterBlockId: string, type?: BlockType, content?: string) => Block;
  onUpdateBlock: (blockId: string, content: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onChangeBlockType: (blockId: string, type: BlockType) => void;
}
export function BlockEditor({
  blocks,
  onAddBlock,
  onUpdateBlock,
  onDeleteBlock,
  onChangeBlockType
}: BlockEditorProps) {
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(null);
  const [slashMenuBlockId, setSlashMenuBlockId] = useState<string | null>(null);
  const [slashFilter, setSlashFilter] = useState('');
  const blockRefs = useRef<Map<string, HTMLElement>>(new Map());
  const pendingFocusId = useRef<string | null>(null);
  const registerRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      blockRefs.current.set(id, el);
      // If this block was pending focus, focus it now
      if (pendingFocusId.current === id) {
        pendingFocusId.current = null;
        setFocusedBlockId(id);
      }
    } else {
      blockRefs.current.delete(id);
    }
  }, []);
  const focusBlock = useCallback((id: string) => {
    const el = blockRefs.current.get(id);
    if (el) {
      setFocusedBlockId(id);
    } else {
      // Block not yet mounted, set pending
      pendingFocusId.current = id;
    }
  }, []);
  const handleEnter = useCallback((blockId: string) => {
    const newBlock = onAddBlock(blockId);
    // Use requestAnimationFrame to wait for React to render the new block
    requestAnimationFrame(() => {
      focusBlock(newBlock.id);
    });
  }, [onAddBlock, focusBlock]);
  const handleDelete = useCallback((blockId: string) => {
    const idx = blocks.findIndex(b => b.id === blockId);
    if (blocks.length <= 1) return; // Don't delete the last block
    const prevBlock = idx > 0 ? blocks[idx - 1] : null;
    onDeleteBlock(blockId);
    if (prevBlock) {
      requestAnimationFrame(() => {
        focusBlock(prevBlock.id);
      });
    }
  }, [blocks, onDeleteBlock, focusBlock]);
  const handleSlashCommand = useCallback((blockId: string, filter: string) => {
    setSlashMenuBlockId(blockId);
    setSlashFilter(filter);
  }, []);
  const handleCloseSlash = useCallback(() => {
    setSlashMenuBlockId(null);
    setSlashFilter('');
  }, []);
  const handleSelectCommand = useCallback((type: BlockType) => {
    if (!slashMenuBlockId) return;
    onChangeBlockType(slashMenuBlockId, type);
    onUpdateBlock(slashMenuBlockId, '');
    setSlashMenuBlockId(null);
    setSlashFilter('');
    requestAnimationFrame(() => {
      focusBlock(slashMenuBlockId);
    });
  }, [slashMenuBlockId, onChangeBlockType, onUpdateBlock, focusBlock]);
  // Track numbering for numbered lists
  let numberedIndex = 0;
  return <div className="space-y-0.5" data-id="element-156">
      {blocks.map(block => {
      if (block.type === 'numberedList') {
        numberedIndex++;
      } else {
        numberedIndex = 0;
      }
      return <div key={block.id} className="relative" data-id="element-157">
            <BlockItem block={block} index={block.type === 'numberedList' ? numberedIndex - 1 : 0} isFocused={focusedBlockId === block.id} onUpdate={content => onUpdateBlock(block.id, content)} onEnter={() => handleEnter(block.id)} onDelete={() => handleDelete(block.id)} onFocus={() => setFocusedBlockId(block.id)} onSlashCommand={filter => handleSlashCommand(block.id, filter)} onCloseSlash={handleCloseSlash} registerRef={registerRef} data-id="element-158" />
            {slashMenuBlockId === block.id && <CommandMenu filter={slashFilter} onSelect={handleSelectCommand} onClose={handleCloseSlash} data-id="element-159" />}
          </div>;
    })}
    </div>;
}